import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnDestroy, ViewChild, inject } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, NgControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { MatInput } from '@angular/material/input';

export class VolumeHoraire {
    constructor(public heures: string, public minutes: string) {}

    public static fromMinutes(valueInMinutes: number) {
        const hours = Math.floor(valueInMinutes / 60);
        const minutes = valueInMinutes % 60;
        return new VolumeHoraire(hours.toString(), minutes.toString());
    }

    public toMinutes(): number {
        return Number(this.heures) * 60 + Number(this.minutes);
    }
}

@Component({
    selector: 'app-volume-horaire-input',
    templateUrl: './volume-horaire-input.component.html',
    styleUrls: ['./volume-horaire-input.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: VolumeHoraireInputComponent }],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id'
    },
    imports: [ReactiveFormsModule, MatInput]
})
export class VolumeHoraireInputComponent implements ControlValueAccessor, MatFormFieldControl<VolumeHoraire>, OnDestroy {
    private _formBuilder = inject(FormBuilder);
    private _focusMonitor = inject(FocusMonitor);
    private _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    _formField = inject<MatFormField>(MAT_FORM_FIELD, { optional: true })!;
    ngControl = inject(NgControl, { optional: true, self: true })!;

    static nextId = 0;
    @ViewChild('heures') heuresInput: HTMLInputElement;
    @ViewChild('minutes') minutesInput: HTMLInputElement;
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('aria-describedby') userAriaDescribedBy: string;
    onChange: (_: unknown) => void = () => {};
    onTouched = () => {};
    stateChanges = new Subject<void>();
    focused = false;
    touched = false;
    controlType = 'odf-volume-horaire-input';
    id = `volume-horaire-input-${VolumeHoraireInputComponent.nextId++}`;

    constructor() {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
    }

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }
    private _placeholder: string;

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }
    private _required = false;

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        if (this._disabled) {
            this.parts.disable();
        } else {
            this.parts.enable();
        }
        this.stateChanges.next();
    }
    private _disabled = false;

    @Input()
    get value(): VolumeHoraire | null {
        if (this.parts.valid) {
            const {
                value: { heures, minutes }
            } = this.parts;
            return new VolumeHoraire(heures!, minutes!);
        }
        return null;
    }
    set value(tel: VolumeHoraire | null) {
        // eslint-disable-next-line prefer-const
        let { heures, minutes } = tel || new VolumeHoraire('', '');
        if (minutes.length === 1) {
            minutes = '0' + minutes;
        }
        this.parts.setValue({ heures, minutes });
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this.parts.invalid && this.touched;
    }

    parts = this._formBuilder.group({
        heures: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(4)]],
        minutes: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2), Validators.max(59)]]
    });

    get empty() {
        const {
            value: { heures, minutes }
        } = this.parts;

        return !heures && !minutes;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    writeValue(tel: VolumeHoraire | null): void {
        this.value = tel;
    }

    registerOnChange(fn: (_: unknown) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }
    setDescribedByIds(ids: string[]): void {
        const controlElement = this._elementRef.nativeElement.querySelector('.volume-horaire-input-container')!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }
    onFocusIn() {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            if (!this.parts.controls.minutes.value) {
                this.parts.controls.minutes.patchValue('00');
                this.parts.markAsDirty();
                this.onChange(this.value);
            } else if (this.parts.controls.minutes.value.length === 1) {
                this.parts.controls.minutes.patchValue('0' + this.parts.controls.minutes.value);
                this.parts.markAsDirty();
                this.onChange(this.value);
            }
            if (!this.parts.controls.heures.value) {
                this.parts.controls.heures.patchValue('0');
                this.parts.markAsDirty();
                this.onChange(this.value);
            }
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
        this.autoFocusNext(control, nextElement);
        this.onChange(this.value);
    }

    autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
        if (control.value.length < 1) {
            this._focusMonitor.focusVia(prevElement, 'program');
        }
    }

    autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
        if (!control.errors && control.value.length === 4 && nextElement) {
            this._focusMonitor.focusVia(nextElement, 'program');
        }
    }

    onContainerClick() {
        // if (this.parts.controls.heures.valid) {
        //     this._focusMonitor.focusVia(this.minutesInput, 'program');
        // } else if (this.parts.controls.minutes.valid) {
        //     this._focusMonitor.focusVia(this.heuresInput, 'program');
        // } else {
        //     this._focusMonitor.focusVia(this.heuresInput, 'program');
        // }
    }
}
